import type { UrlObject } from 'node:url';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { AGENT_BUILDER_ROUTE, ASK_PERCY_ROUTE, CHAT_SESSION_PARAM, CHAT_SESSION_ROUTE, chatSessionUrl, INVESTIGATION_CHAT_PARAM, INVESTIGATION_ROUTE, investigationUrl, PLAYBOOK_ROUTE } from '@/config/constants';
import useAssumeToViewObject from '@/hooks/use-assume-to-view-object';
import { getLastSessionId, setLastSessionId } from '../../hooks/use-chat-widget-last-session';
import useChatWidgetLockedFullscreen from '../../hooks/use-chat-widget-locked-fullscreen';
import useResetChatOnTenantSwitch from '../../hooks/use-reset-chat-on-tenant-switch';
import { rememberChatSessionEnv } from '../../utils/chat-session-stash';
import type { InvestigationChatScope } from './investigation-scope-context';
type UseChatWidgetSessionArgs = {
  enabled: boolean;
  scope: InvestigationChatScope | null;
  /** Owning tenant — scopes the persisted last-session so a switch can't restore another tenant's chat. */
  orgId?: string;
};

// Owns the chat widget's session + route logic. `mintingRef` guards the route-driven effects from
// reverting a just-adopted session while its navigation is in flight.
const useChatWidgetSession = ({
  enabled,
  scope,
  orgId
}: UseChatWidgetSessionArgs) => {
  const router = useRouter();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const routeSessionId = router.pathname === CHAT_SESSION_ROUTE ? router.query[CHAT_SESSION_PARAM] as string | undefined : undefined;
  const routeChat = useAssumeToViewObject(routeSessionId);
  const routeChatTenantId = routeChat.data?.tenantId;
  const isAssumingRouteTenant = routeChat.fetchStatus !== 'idle' || !!routeChatTenantId && routeChatTenantId !== orgId;
  const restoredRef = useRef(false);
  const mintingRef = useRef(false);

  // Drop a stale session + redirect off `/chat/{id}` when the tenant changes in place.
  useResetChatOnTenantSwitch({
    orgId,
    sessionId,
    setSessionId,
    restoredRef,
    mintingRef,
    routeChatTenantId
  });

  // Shell routes present the chat via lockedFullscreen (route-implied open in effectiveOpen);
  // nothing here needs to force the persisted open flag.
  const lockedFullscreen = useChatWidgetLockedFullscreen();

  // Deep link: seed sessionId from the `/chat/[id]` route, once a firm employee is in the chat's tenant.
  useEffect(() => {
    if (!enabled || mintingRef.current || isAssumingRouteTenant) return;
    if (routeSessionId && routeSessionId !== sessionId) {
      if (routeChat.data?.context.object === 'chat_session' && routeChat.data.isLive !== null) {
        rememberChatSessionEnv(routeSessionId, routeChat.data.isLive);
      }
      setSessionId(routeSessionId);
    }
  }, [enabled, routeSessionId, sessionId, isAssumingRouteTenant]);

  // A chat carried onto `/ask-percy` lives only in state — reflect it at `/chat/{id}` so the URL
  // names the active chat. The empty composer (no sessionId) stays on `/ask-percy`. Coming from
  // `/chat/{id}` itself (the logo, the nav item) means a new chat, not a bounce back.
  const prevPathnameRef = useRef(router.pathname);
  useEffect(() => {
    const leftChat = prevPathnameRef.current === CHAT_SESSION_ROUTE;
    prevPathnameRef.current = router.pathname;
    if (!router.isReady || mintingRef.current) return;
    if (router.pathname === ASK_PERCY_ROUTE && sessionId) {
      if (leftChat) {
        setSessionId(null);
        return;
      }
      mintingRef.current = true;
      router.replace(chatSessionUrl(sessionId)).finally(() => {
        mintingRef.current = false;
      });
    }
  }, [router.isReady, router.pathname, sessionId]);
  const carriesChat = router.pathname === INVESTIGATION_ROUTE || router.pathname === AGENT_BUILDER_ROUTE || router.pathname === PLAYBOOK_ROUTE;
  const carriedChatId = router.isReady && carriesChat ? router.query[INVESTIGATION_CHAT_PARAM] as string | undefined : undefined;

  // Seed sessionId from `?chat=`.
  useEffect(() => {
    if (!enabled || mintingRef.current) return;
    if (carriedChatId && carriedChatId !== sessionId) setSessionId(carriedChatId);
  }, [enabled, carriedChatId, sessionId]);

  // Unless the URL names the chat, restore the last session from localStorage so a reload keeps it
  // open. An investigation opened from the list has no `?chat=`, so it restores too. A URL-named chat
  // still spends the one shot, or New chat later would re-adopt what localStorage holds. An active
  // session only defers it: the tenant-switch reset clears the session a commit after re-arming this.
  const urlOwnsSession = router.pathname === CHAT_SESSION_ROUTE || !!carriedChatId;
  useEffect(() => {
    if (restoredRef.current || !enabled || !router.isReady || mintingRef.current || !orgId || sessionId) return;
    restoredRef.current = true;
    if (urlOwnsSession) return;
    const last = getLastSessionId(orgId);
    if (last) setSessionId(last);
  }, [enabled, router.isReady, urlOwnsSession, sessionId, orgId]);

  // Mirror the active session to localStorage so the restore above can pick it up on a reload.
  const mirroredOrgIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !router.isReady || !orgId) return;
    // Skip the write on the commit where the tenant just changed: sessionId is still the prior
    // tenant's id, so writing it would pin a cross-tenant session for the restore to re-adopt.
    if (mirroredOrgIdRef.current !== orgId) {
      mirroredOrgIdRef.current = orgId;
      return;
    }
    setLastSessionId(sessionId, orgId);
  }, [enabled, router.isReady, sessionId, orgId]);

  // New chat: empty composer. On `/chat/[id]` navigate to `/ask-percy`; on routes that carry the
  // chat in `?chat=` (investigation, agent builder, playbook) drop the param so the seed effect doesn't re-adopt it.
  const handleNewChat = () => {
    setSessionId(null);
    if (router.pathname === CHAT_SESSION_ROUTE) {
      mintingRef.current = true;
      router.push(ASK_PERCY_ROUTE).finally(() => {
        mintingRef.current = false;
      });
    } else if (carriedChatId) {
      const {
        [INVESTIGATION_CHAT_PARAM]: _chat,
        ...query
      } = router.query;
      syncUrl({
        pathname: router.pathname,
        query
      });
    }
  };

  // mintingRef guards the route-driven effects from reverting while the in-place navigation lands.
  const syncUrl = (href: string | UrlObject) => {
    mintingRef.current = true;
    router.replace(href).finally(() => {
      mintingRef.current = false;
    });
  };
  const investigationId = scope?.investigationId;

  // Adopt a session (minted or picked) and reflect it in the URL per route.
  const adoptSession = (id: string) => {
    setSessionId(id);
    if (router.pathname === ASK_PERCY_ROUTE || router.pathname === CHAT_SESSION_ROUTE) {
      syncUrl(chatSessionUrl(id));
    } else if (router.pathname === INVESTIGATION_ROUTE && investigationId) {
      syncUrl(investigationUrl(investigationId, {
        chatSessionId: id
      }));
    } else if (router.pathname === AGENT_BUILDER_ROUTE || router.pathname === PLAYBOOK_ROUTE) {
      syncUrl({
        pathname: router.pathname,
        query: {
          ...router.query,
          [INVESTIGATION_CHAT_PARAM]: id
        }
      });
    }
  };
  return {
    sessionId,
    isAssumingRouteTenant,
    lockedFullscreen,
    adoptSession,
    handleNewChat
  };
};
export default useChatWidgetSession;